import { selectors, classes } from './constants.js';
import { coalesceAfterPaint } from '@/helpers/afterPaint';

export default {
    async setCurrentVariant(event = null) {
        this.currentVariant = event?.dataset?.variant || null;
        if (this.currentVariant) {
            await this.onVariantChange();
            return;
        }

        const option1 = event.dataset?.options[0]?.value;
        const option2 = event.dataset?.options[1]?.value;
        if (![
            !option1,
            !option2,
        ]?.includes(false)) {
            await this.onVariantChange();

            return;
        }

        const option1_option2_existing_variant = this.product.variants.find((variant) => ![
            variant?.option1 === option1,
            variant?.option2 === option2
        ]?.includes(false));

        if (option1_option2_existing_variant) {
            this.currentVariant = option1_option2_existing_variant;
        } else {
            const option1_existing_variant = this.product.variants.find((variant) => variant.option1 === option1);

            if (option1_existing_variant) {
                this.currentVariant = option1_existing_variant;
            }
        }

        if (this.currentVariant) {
            await this.onVariantChange();
            return;
        }

        this.currentVariant = this.product?.first_available_variant;

        await this.onVariantChange();
    },

    async onVariantChange() {
        // Critical, above-the-fold updates that must paint WITH the interaction
        // (price, option availability/selection, ATC, title, stock). Kept
        // synchronous so the selection feels instant.
        this.updateOptionSelectors();
        this.updateSelectedOptionValues();
        this.updateSelectedColorSwatches();
        this.updateProductSelect();
        this.updatePrices();
        this.updateATCButton();
        this.updateStockState();
        this.updateTitle();
        this.updateVariantTitle();

        // Fire-and-forget (intentionally NOT awaited): the gallery refetch/rebuild
        // must not block the interaction. It kicks off its network/cache lookup
        // now and self-defers the DOM swap to after paint. See display.js updateSlider.
        this.updateSlider();

        // Non-critical / heavier updates (history, discount tiers, accordion
        // rebuild, quantity limits, coupon note, compare, options-slider relayout)
        // run once AFTER the next paint so the selection paints first. Coalesced,
        // so rapid option changes only run the tail once, reading the latest state.
        this.deferVariantUpdates();

        document.dispatchEvent(new Event('drawers:close'));
    },

    deferVariantUpdates() {
        this.runDeferredVariantUpdates = this.runDeferredVariantUpdates || coalesceAfterPaint(() => {
            this.updateURL();
            this.updateBoxFeaturesMbDiscountTiers();
            this.updateQuantityLimits();
            this.updateCouponNote();
            this.updateProductCompare();
            this.updateAccordions();
            this.updateOptionsSliders();
        });

        this.runDeferredVariantUpdates();
    },

    // Swap an option thumbnail's source AFTER the new image has decoded off the
    // interaction frame, so the image decode/paint cost stays out of the variant
    // change's presentation-delay window (the swatch swap was the largest single
    // INP cost measured — paint-bound, not JS). Falls back to a direct swap when
    // decode() is unavailable or rejects, and skips no-op swaps.
    swapImageDecoded(imgEl, url, { alt = null, srcset = false } = {}) {
        if (!imgEl || !url) {
            return;
        }

        if (imgEl.getAttribute('src') === url) {
            if (alt != null) {
                imgEl.alt = alt;
            }

            return;
        }

        const apply = () => {
            if (!srcset) {
                // images were retaining previous srcset and not updating
                imgEl.removeAttribute('srcset');
                imgEl.removeAttribute('sizes');
            }

            imgEl.src = url;

            if (srcset) {
                imgEl.srcset = url;
            }

            if (alt != null) {
                imgEl.alt = alt;
            }
        };

        if (typeof imgEl.decode !== 'function') {
            apply();

            return;
        }

        const preloader = new Image();
        preloader.src = url;
        preloader.decode().then(apply).catch(apply);
    },

    updateOptionSelectors() {
        const { product } = this;
        if (!product) {
            return;
        }

        const variants = product?.variants;
        if (!variants) {
            return;
        }

        if (!variants?.length) {
            return;
        }

        const { currentVariant } = this;
        if (!currentVariant) {
            return;
        }

        const option1 = currentVariant?.option1;
        if (!option1) {
            return;
        }

        const option2ValueSelectors = document.querySelectorAll(`[data-form="${this.productFormId}"]${selectors.option2ValueSelectors}`);
        if (!option2ValueSelectors) {
            return;
        }

        if (!option2ValueSelectors?.length) {
            return;
        }

        option2ValueSelectors?.forEach((el) => {
            if (this.isMobile) {
               el.closest(`.${classes?.swiperSlide}`).classList.remove(classes?.hide);
            } else {
                el?.classList?.remove(classes?.hide);
            }
        });

        const existing_option2_values = [
            ...new Set(variants?.filter((variant) => variant?.option1 === option1)?.map((variant) => variant?.option2) || []),
        ];

        option2ValueSelectors?.forEach((el) => {
            const value = el?.dataset?.option2Value || '';
            if (!value) {
                return;
            }

            if (!existing_option2_values?.includes(value)) {
                if (this.isMobile) {
                    el?.closest(`.${classes?.swiperSlide}`)?.classList?.add(classes?.hide);
                 } else {
                     el?.classList?.add(classes?.hide);
                 }

                return;
            }

            if (value === currentVariant?.option2) {
                const radio = el?.querySelector('input');
                if (radio && !this.isMobile) {
                    radio.checked = true;
                }
            }

            const optionImageContainer = el?.querySelector(selectors?.optionImage);
            if (!optionImageContainer) {
                return;
            }

            const alt = `${this.currentVariant.option1} / ${value}`;
            const optionImageURL = [
                this.product.images.find((image) => {
                    return !!image?.alt?.includes(alt);
                }),
                this.product.featured_image,
            ]?.find((image) => {
                return !!image?.src;
            })?.src;

            const optionImage = optionImageContainer?.querySelector('img');

            if (!optionImageURL && optionImage) {
                optionImage.remove();

                return;
            }

            const optionImageURLSized = `${optionImageURL}&width=225`;

            if (optionImage) {
                this.swapImageDecoded(optionImage, optionImageURLSized, { alt, srcset: true });

                if (this.isMobile) {
                    el.closest(`.${classes?.swiperSlide}`).classList.remove(classes?.hide);
                 } else {
                     el?.classList?.remove(classes?.hide);
                 }

                return;
            }

            optionImageContainer.innerHTML = `<img
                src="${optionImageURLSized}"
                alt="${alt}"
                width="225"
                height="225"
                class="absolute-img"
            />`;

            if (this.isMobile) {
                el.closest(`.${classes?.swiperSlide}`).classList.remove(classes?.hide);
             } else {
                 el?.classList?.remove(classes?.hide);
             }
        });

        this.updateAvailableNumberOfOptions(existing_option2_values.length, 2);

        const { option2 } = this.currentVariant;
        if (!option2) {
            return;
        }

        const option3ValueSelectors = document.querySelectorAll(`[data-form="${this.productFormId}"]${selectors.option3ValueSelectors}`);
        if (!option3ValueSelectors) {
            return;
        }

        if (!option3ValueSelectors?.length) {
            return;
        }

        option3ValueSelectors?.forEach((el) => {
            if (this.isMobile) {
                el.closest(`.${classes?.swiperSlide}`).classList.remove(classes?.hide);
            } else {
                el?.classList?.remove(classes?.hide);
            }

        });

        const existing_option3_values = [
            ...new Set(this.product.variants.filter((variant) => ![
                variant?.option1 === option1,
                variant?.option2 === option2
            ]?.includes(false)).map((variant) => variant?.option3) || [])
        ];

        option3ValueSelectors.forEach((el) => {
            const value = el?.dataset?.option3Value || '';

            if (existing_option3_values.includes(value)) {
                if (value === this.currentVariant.option3) {
                    const radio = el.querySelector('input');
                    if (radio && !this.isMobile) {
                        radio.checked = true;
                    }
                }

                const optionImageContainer = el.querySelector(selectors?.optionImage);
                if (!optionImageContainer) {
                    return;
                }

                const alt = [
                    currentVariant?.option1,
                    currentVariant?.option2,
                    value
                ]
                    .filter((part) => Boolean(part))
                    .join(' / ');

                const optionImageURL = (
                    (
                        variants?.reduce((images, variant) => {
                            const image = variant?.image;
                            if (!image) {
                                return images;
                            }

                            return [...images, image];
                        }, []) ||
                        product?.images ||
                        []
                    )?.find((img) => img?.alt?.toLowerCase().includes(alt?.toLowerCase())) || product?.featured_image
                )?.src;

                const optionImage = optionImageContainer.querySelector('img');
                if (!optionImageURL) {
                    if (optionImage) {
                        optionImage.remove();
                    }

                    return;
                }

                const optionImageURLSized = `${optionImageURL}&width=225`;

                if (optionImage) {
                    this.swapImageDecoded(optionImage, optionImageURLSized, { alt, srcset: false });
                    return;
                }

                optionImageContainer.innerHTML = `<img
                    src="${optionImageURLSized}"
                    alt="${alt}"
                    width="225"
                    height="225"
                    class="absolute-img"
                >`;

                return;
            }

            if (this.isMobile) {
                el.closest(`.${classes?.swiperSlide}`).classList.add(classes?.hide);
             } else {
                 el?.classList?.add(classes?.hide);
             }
        });

        this.updateAvailableNumberOfOptions(existing_option3_values.length, 3);
    },

    updateAvailableNumberOfOptions(numberOfOptions, index) {
        if ([
            !numberOfOptions,
            !index,
            !this.productFormId
        ]?.includes(true)) {
            return;
        }

        const el = document.querySelector(`[data-form="${this.productFormId}"][data-index="option${index}"]${selectors?.numberOfAvailableOptions}`);
        if (!el) {
            return;
        }

        const option_name =
            this.product?.options
                ?.find((option) => option.position === index)
                ?.name?.toLowerCase() ?? '';
        const s = option_name && numberOfOptions === 1 ? '' : 's';

        el.innerHTML = `${numberOfOptions} ${option_name}${s}`.trim();
    },

    updateSelectedOptionValues() {
        const {currentVariant} = this;
        if (!currentVariant) {
            return;
        }

        const selectedOptionValues = document.querySelectorAll(`[data-form="${this.productFormId}"]${selectors.selectedOptionValues}`);

        if (!selectedOptionValues) {
            return;
        }

        if (!selectedOptionValues?.length) {
            return;
        }

        selectedOptionValues?.forEach((el) => {

            const option_index = el?.dataset?.index;

            if (!option_index) {
                return;
            }

            const keys = Object.keys(currentVariant);


            if (!keys) {
                return;
            }

            if (!keys?.length) {
                return;
            }

            if (!keys?.includes(option_index)) {
                return;
            }

            let value = '';

            if (option_index === 'selling_plan_allocations') {
                value = this.purchaseType === 'subscription' ? 'Subscription Box' : 'One-Time Purchase';
            } else {
                value = currentVariant[option_index];
            }

            if (!value) {
                return;
            }

            el.innerHTML = value;
        });
    },

    updateSelectedColorSwatches() {
        const { container } = this;
        if (!container) {
            return;
        }

        const { settings } = this;
        if (!settings) {
            return;
        }

        const colors = settings?.colors;
        if (!colors) {
            return;
        }

        if (!colors?.length) {
            return;
        }

        const selectedColorSwatches = Array.from(container.querySelectorAll(`${selectors?.selectedColorSwatches}`));
        if (!selectedColorSwatches) {
            return;
        }

        if (!selectedColorSwatches?.length) {
            return;
        }

        selectedColorSwatches.forEach((el) => {
            const option_index = el?.dataset?.index;
            if (!option_index) {
                return;
            }

            const value = this.currentVariant[option_index];
            if (!value) {
                return;
            }

            const color = colors.find((color) => color?.color_name?.toLowerCase() === value?.toLowerCase());

            if (!color) {
                return;
            }

            if (!color.hex_color_code) {
                el.style.backgroundImage = 'initial';
                el.style.backgroundColor = '#e2e2e2';

                return;
            }

            if (color?.color_image_url) {
                el.style.backgroundImage = `url(${color.color_image_url}&width=16)`;
            } else {
                el.style.backgroundImage = 'initial';
                el.style.backgroundColor = color?.hex_color_code || '#e2e2e2';
            }
        });
    },

    updateProductSelect() {
        const {currentVariant} = this;
        if (!currentVariant) {
            return;
        }

        const id = currentVariant?.id;
        if (!id) {
            return;
        }

        const {productSelect} = this;
        if (!productSelect) {
            return;
        }

        productSelect.value = id;

    },

    getOptionsFromVariant(variant) {
        return Object.keys(variant)?.filter((key) => ![
            key?.toLowerCase() !== 'options',
            !!key?.toLowerCase()?.startsWith('option'),
        ]?.includes(false)).reduce((options, key) => ({
            ...options,
            [key]: variant[key],
        }), {}) || {};
    },

    getOptionNamesFromProduct(product) {
        const options_with_values = product?.options_with_values;
        if (!options_with_values) {
            return null;
        }

        if (!options_with_values?.length) {
            return null;
        }

        return options_with_values?.reduce((options, option) => {
            const name = option?.name;
            if (!name) {
                return options;
            }

            const position = option?.position;
            if (!position) {
                return options;
            }

            return {
                ...options,
                [`option${position}`]: name,
            };
        }, {}) || {};
    },

    updateURL() {
        const {currentVariant} = this;
        if (!currentVariant) {
            return;
        }

        const id = currentVariant?.id;
        if (!id) {
            return;
        }

        const url = new URL(window?.location?.href || "/");
        if (!url) {
            return;
        }

        url?.searchParams?.set('variant', id);

        window?.history?.replaceState({}, '', url);
    },

    updateVariantTitle() {
        const {currentVariant} = this;
        if (!currentVariant) {
            return;
        }

        const title = currentVariant?.title;
        if (!title) {
            return;
        }

        const variantTitles = document.querySelectorAll(`[data-variant-title="${ this.product.id }"]`);
        if (!variantTitles) {
            return;
        }

        if (!variantTitles?.length) {
            return;
        }

        variantTitles?.forEach((el) => {
            el.innerHTML = title;
        });
    },

    updateTitle() {
        const { container } = this;
        if (!container) {
            return;
        }

        const titleOptions = Array?.prototype?.slice?.apply(container?.querySelectorAll(selectors?.titleOption) || []);
        if (!titleOptions) {
            return;
        }

        if (!titleOptions?.length) {
            return;
        }

        const {currentVariant} = this;
        if (!currentVariant) {
            return;
        }

        const selectedOptions = {
            option1: currentVariant?.option1,
            option2: currentVariant?.option2,
            option3: currentVariant?.option3,
        };

        const keys = Object.keys(selectedOptions);
        if (!keys) {
            return;
        }

        if (!keys?.length) {
            return;
        }

        titleOptions?.forEach((titleOption) => {
            const option = titleOption?.dataset?.option;
            if (!option) {
                return;
            }

            if (!keys?.includes(option)) {
                return;
            }

            const value = selectedOptions[option];
            if (!value) {
                return;
            }

            titleOption.innerHTML = value;
        });
    },

    updateProductCompare() {
        const { container } = this;
        if (!container) {
            return;
        }

        const els = container.querySelectorAll(selectors.productCompareAddAndOpen);
        if (!els?.length) {
            return;
        }

        const { product } = this;
        if (!product) {
            return;
        }

        const { handle } = product;
        if (!handle) {
            return;
        }

        const {currentVariant} = this;
        if (!currentVariant) {
            return;
        }

        const id = currentVariant?.id;
        if (!id) {
            return;
        }

        const value = `${handle}:${id}`;
        els.forEach((el) => {
            el.setAttribute('data-value', value);
        });
    },
};
